import {
  WorkflowCatalogIdentityCellComponent,
  WorkflowCatalogSortLabelComponent,
} from './workflow-catalog-controls.component';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WorkflowFamilyVisibilityComponent, type WorkflowFamilyVisibilityState, type WorkflowFamilyVisibilityRequest } from './workflow-family-visibility.component';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  WorkflowFamilyActionsCellComponent,
  WorkflowFamilyActionsState,
} from './workflow-family-actions-cell.component';
import { WorkflowVersionActionsCellComponent } from './workflow-version-actions-cell.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonConfig, InputSelectOption } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import {
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';

import {
  WorkflowDefinitionCatalogPage,
  WorkflowDefinitionCatalogQuery,
  WorkflowDefinitionLifecycleDetail,
  WorkflowDefinitionFamilyPage,
  WorkflowDefinitionFamilySummary,
  WorkflowDefinitionFamilyVisibility,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowRegistryDiagnosticDisplayError } from './platform-workflow-registry-diagnostics.models';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS: InputSelectOption[] = [
  { value: 10, viewValue: '10' },
  { value: 25, viewValue: '25' },
  { value: 50, viewValue: '50' },
  { value: 100, viewValue: '100' },
];
const LIFECYCLE_OPTIONS: InputSelectOption[] = [
  { value: '', viewValue: 'All lifecycle states' },
  { value: 'draft', viewValue: 'Draft' },
  { value: 'published', viewValue: 'Published' },
  { value: 'active', viewValue: 'Active' },
  { value: 'retired', viewValue: 'Retired' },
];
const ACTIVATION_OPTIONS: InputSelectOption[] = [
  { value: '', viewValue: 'All activation states' },
  { value: 'active', viewValue: 'Active' },
  { value: 'scheduled', viewValue: 'Scheduled' },
  { value: 'expired', viewValue: 'Expired' },
  { value: 'retired', viewValue: 'Retired' },
  { value: 'not_activated', viewValue: 'Not activated' },
];

export interface WorkflowDefinitionCatalogFilterValues {
  search: string;
  workflowCode: string;
  lifecycleStatusCode: string;
  activationPostureCode: string;
  pageSize: number;
}

type WorkflowDefinitionCatalogPrimaryFilterValues = Pick<
  WorkflowDefinitionCatalogFilterValues,
  'search' | 'lifecycleStatusCode' | 'activationPostureCode'
>;

type WorkflowDefinitionCatalogAdvancedFilterValues = Pick<
  WorkflowDefinitionCatalogFilterValues,
  'workflowCode' | 'pageSize'
>;

export interface WorkflowDefinitionCatalogSelection {
  workflowCode: string;
  definitionVersionCode: string;
}

@Component({
  selector: 'phms-platform-workflow-registry-catalog',
  imports: [
    UiButtonDirective,
    UiControlDirective,
    FormsModule,
    WorkflowFamilyVisibilityComponent,
    CommonModule,
    UiLibraryModule,
    EzStandardStateComponent,
  ],
  templateUrl: './platform-workflow-registry-catalog.component.html',
  styleUrl: './platform-workflow-registry-catalog.component.scss',
})
export class PlatformWorkflowRegistryCatalogComponent implements OnChanges {
  private gridApi?: GridApi<WorkflowDefinitionLifecycleDetail>;
  private familyGridApi?: GridApi<WorkflowDefinitionFamilySummary>;
  private currentQuery: WorkflowDefinitionCatalogQuery = {
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };
  private currentSelection: WorkflowDefinitionCatalogSelection | null = null;
  private hasHydratedQuery = false;

  @Input() page: WorkflowDefinitionCatalogPage | null = null;
  @Input() error: WorkflowRegistryDiagnosticDisplayError | null = null;
  @Input() loading = false;
  @Input() canAuthor = false;
  @Input() draftActionBlocked = false;
  @Input() familyMode = false;
  @Input() familyPage: WorkflowDefinitionFamilyPage | null = null;
  @Input() family: WorkflowDefinitionFamilySummary | null = null;
  @Input() visibilityDetails: Readonly<Record<string, WorkflowDefinitionFamilyVisibility>> = {};
  @Input() visibilityStates: Readonly<Record<string, WorkflowFamilyVisibilityState>> = {};
  @Input() visibilityContext: (code: string) => string = () => '';

  @Input()
  set selectedDefinition(value: WorkflowDefinitionCatalogSelection | null) {
    this.currentSelection = value;
    this.syncGridSelection();
  }

  get selectedDefinition(): WorkflowDefinitionCatalogSelection | null {
    return this.currentSelection;
  }

  @Input()
  set query(value: WorkflowDefinitionCatalogQuery | null | undefined) {
    const normalized = normalizeCatalogQuery(value);
    const advancedFiltersChanged =
      this.currentQuery.workflowCode !== normalized.workflowCode ||
      this.currentQuery.pageSize !== normalized.pageSize;
    const hasAdvancedFilterValue =
      Boolean(normalized.workflowCode) ||
      normalized.pageSize !== DEFAULT_PAGE_SIZE;
    this.currentQuery = normalized;
    this.primaryFilterFormValues = {
      search: normalized.search ?? '',
      lifecycleStatusCode: normalized.lifecycleStatusCode ?? '',
      activationPostureCode: normalized.activationPostureCode ?? '',
    };
    this.advancedFilterFormValues = {
      workflowCode: normalized.workflowCode ?? '',
      pageSize: normalized.pageSize,
    };
    if (
      (!this.hasHydratedQuery || advancedFiltersChanged) &&
      hasAdvancedFilterValue
    ) {
      this.advancedFiltersOpen = true;
    }
    this.hasHydratedQuery = true;
  }

  get query(): WorkflowDefinitionCatalogQuery {
    return this.currentQuery;
  }

  @Output() readonly queryChange =
    new EventEmitter<WorkflowDefinitionCatalogQuery>();
  @Output() readonly retryRequested = new EventEmitter<void>();
  @Output() readonly definitionSelected =
    new EventEmitter<WorkflowDefinitionLifecycleDetail>();
  @Output() readonly draftRequested =
    new EventEmitter<WorkflowDefinitionLifecycleDetail>();
  @Output() readonly familySelected = new EventEmitter<WorkflowDefinitionFamilySummary>();
  @Output() readonly allFamiliesRequested = new EventEmitter<void>();
  @Output() readonly familyDraftsRequested = new EventEmitter<string>();
  @Output() readonly familyArchivedDraftsRequested = new EventEmitter<string>();
  @Output() readonly visibilityRequested = new EventEmitter<WorkflowFamilyVisibilityRequest>();
  @Output() readonly visibilityRefreshRequested = new EventEmitter<string>();
  @Output() readonly visibilityRetryRequested = new EventEmitter<string>();
  familyVisibility(code: string, fallback?: WorkflowDefinitionFamilyVisibility | null): WorkflowDefinitionFamilyVisibility | null {
    return this.visibilityDetails[code] ?? fallback ?? null;
  }
  setIncludeHidden(include: boolean): void {
    if (this.familyMode && !this.loading && this.familyPage?.sharedVisibilityAvailable === true)
      this.emitQuery({ ...this.currentQuery, includeHidden: include ? 'true' : undefined, pageNumber: 1 });
  }

  selectFamily(family: WorkflowDefinitionFamilySummary): void {
    if (this.familyMode && !this.loading && !this.error && this.familyPage?.items.includes(family)) this.familySelected.emit(family);
  }

  get familyRows(): WorkflowDefinitionFamilySummary[] {
    return this.isForbidden ? [] : (this.familyPage?.items ?? []);
  }

  familyActionState(code: string | undefined): WorkflowFamilyActionsState | null {
    if (!this.familyMode || this.isForbidden || !code) return null;
    const rows = this.familyRows.filter((row) => row.workflowCode === code);
    if (rows.length !== 1) return null;
    const row = rows[0];
    return {
      row,
      blocked: this.loading || !!this.error,
      canAuthor: this.canAuthor,
      visibility: this.familyVisibility(code, row.visibility),
      visibilityAvailable: this.familyPage?.sharedVisibilityAvailable === true,
      visibilityContext: this.visibilityContext(code),
      visibilityState: this.visibilityStates[code],
    };
  }

  get historyActionButtons(): ButtonConfig[] {
    const disabled = this.loading || !!this.error;
    return [
      { id: 'allWorkflows', buttonText: 'All workflows', disabled },
      {
        id: 'openFamilyDrafts',
        buttonText: `Open drafts${this.family ? ` (${this.family.openDraftCount})` : ''}`,
        disabled,
        extraData: { workflowCode: this.query.workflowCode },
      },
    ];
  }

  onHistoryActionButtonClick(button: ButtonConfig): void {
    if (
      this.familyMode ||
      this.loading ||
      this.error ||
      !this.query.workflowCode
    )
      return;
    if (button.id === 'allWorkflows') this.allFamiliesRequested.emit();
    else if (
      button.id === 'openFamilyDrafts' &&
      button.extraData?.['workflowCode'] === this.query.workflowCode
    )
      this.openFamilyDrafts(this.query.workflowCode);
  }

  openFamilyDrafts(workflowCode: string, archived = false): void {
    if (!this.loading && !this.error && (this.familyMode ? this.familyPage?.items.some(row => row.workflowCode === workflowCode) : this.query.workflowCode === workflowCode))
      (archived ? this.familyArchivedDraftsRequested : this.familyDraftsRequested).emit(workflowCode);
  }
  searchFamilies(): void {
    if (this.familyMode && !this.loading) this.emitQuery({ search: normalizeOptionalText(this.primaryFilterFormValues.search), includeHidden: this.currentQuery.includeHidden, pageNumber: 1, pageSize: this.currentQuery.pageSize });
  }
  clearFamilySearch(): void {
    if (!this.familyMode || this.loading) return;
    this.primaryFilterFormValues.search = '';
    this.emitQuery({ ...this.currentQuery, search: undefined, pageNumber: 1 });
  }
  familyPageChange(direction: number): void {
    const page = this.familyPage;
    if (!this.familyMode || !page || this.loading) return;
    const pageNumber = page.pageNumber + direction;
    if (pageNumber >= 1 && (pageNumber - 1) * page.pageSize < page.totalCount)
      this.emitQuery({ ...this.currentQuery, pageNumber });
  }

  ngOnChanges(): void {
    this.activeFamilyGridApi()?.refreshCells?.({ columns: ['familyVisibility', 'familyActions'], force: true });
    this.activeGridApi()?.refreshCells?.({
      columns: ['workflowActions'],
      force: true,
    });
  }

  readonly primaryFilterFormConfig: InputConfig[] = [
    {
      name: 'search',
      type: EzFormInputType.TEXT,
      label: 'Search',
      placeholder: 'Name, workflow, or version',
      row: 1,
      width: '2x',
    },
    {
      name: 'lifecycleStatusCode',
      type: EzFormInputType.SELECT,
      label: 'Lifecycle',
      options: LIFECYCLE_OPTIONS,
      row: 1,
      width: '1x',
    },
    {
      name: 'activationPostureCode',
      type: EzFormInputType.SELECT,
      label: 'Activation',
      options: ACTIVATION_OPTIONS,
      row: 1,
      width: '1x',
    },
  ];

  readonly advancedFilterFormConfig: InputConfig[] = [
    {
      name: 'workflowCode',
      type: EzFormInputType.TEXT,
      label: 'Exact workflow code',
      placeholder: 'For example, phms-intake-lifecycle',
      row: 1,
      width: '2x',
    },
    {
      name: 'pageSize',
      type: EzFormInputType.SELECT,
      label: 'Rows per page',
      options: PAGE_SIZE_OPTIONS,
      row: 1,
      width: '1x',
    },
  ];

  primaryFilterFormValues: WorkflowDefinitionCatalogPrimaryFilterValues = {
    search: '',
    lifecycleStatusCode: '',
    activationPostureCode: '',
  };

  advancedFilterFormValues: WorkflowDefinitionCatalogAdvancedFilterValues = {
    workflowCode: '',
    pageSize: DEFAULT_PAGE_SIZE,
  };

  get filterFormValues(): WorkflowDefinitionCatalogFilterValues {
    return {
      ...this.primaryFilterFormValues,
      ...this.advancedFilterFormValues,
    };
  }

  set filterFormValues(value: WorkflowDefinitionCatalogFilterValues) {
    this.primaryFilterFormValues = {
      search: value.search,
      lifecycleStatusCode: value.lifecycleStatusCode,
      activationPostureCode: value.activationPostureCode,
    };
    this.advancedFilterFormValues = {
      workflowCode: value.workflowCode,
      pageSize: value.pageSize,
    };
  }

  advancedFiltersOpen = false;

  readonly familyColumnDefs: ColDef<WorkflowDefinitionFamilySummary>[] = [
    {
      field: 'displayName',
      headerName: 'Workflow',
      flex: 1.4,
      minWidth: 160,
      valueGetter: ({ data }) => data?.displayName || data?.workflowCode || '',
      tooltipValueGetter: ({ data }) =>
        data
          ? `${data.displayName || data.workflowCode} (${data.workflowCode})`
          : '',
    },
    {
      field: 'workflowCode',
      headerName: 'Code',
      flex: 1.2,
      minWidth: 160,
      tooltipField: 'workflowCode',
    },
    {
      field: 'publishedVersionCount',
      headerName: 'Published',
      width: 100,
      tooltipValueGetter: ({ value }) => `${value} published versions`,
    },
    {
      field: 'retainedVersionCount',
      headerName: 'Retained',
      width: 95,
      tooltipValueGetter: ({ value }) => `${value} retained versions`,
    },
    { field: 'openDraftCount', headerName: 'Open drafts', width: 105 },
    { field: 'archivedDraftCount', headerName: 'Archived drafts', width: 125 },
    {
      field: 'latestPublishedDefinitionVersionCode',
      headerName: 'Latest version',
      width: 140,
      valueGetter: ({ data }) =>
        data?.latestPublishedDefinitionVersionCode ||
        (data?.retainedVersionCount
          ? 'No recorded publication'
          : 'Not published'),
      tooltipValueGetter: ({ value }) => String(value ?? ''),
    },
    {
      field: 'latestPublishedUtc',
      headerName: 'Published date',
      width: 140,
      valueFormatter: ({ value }) => formatDateLabel(value),
      tooltipValueGetter: ({ value }) => formatDateTimeLabel(value),
    },
    {
      colId: 'familyVisibility',
      headerName: 'Visibility',
      width: 140,
      valueGetter: ({ data }) => this.familyVisibilityLabel(data),
      tooltipValueGetter: ({ data }) => this.familyVisibilityLabel(data),
    },
    {
      colId: 'familyActions',
      headerName: 'Actions',
      pinned: 'right',
      lockPinned: true,
      width: 115,
      minWidth: 115,
      maxWidth: 115,
      resizable: false,
      sortable: false,
      cellRenderer: WorkflowFamilyActionsCellComponent,
      cellClass: 'workflow-family-actions-cell',
      cellStyle: { overflow: 'visible', whiteSpace: 'normal' },
      cellRendererParams: (params: ICellRendererParams<WorkflowDefinitionFamilySummary>) => {
        const code = params.data?.workflowCode;
        const state = () => this.familyActionState(code);
        return {
          state,
          suppressMouseEventHandling: () => true,
          versions: () => {
            const current = state();
            if (current && current.row.retainedVersionCount > 0)
              this.selectFamily(current.row);
          },
          drafts: (archived: boolean) => {
            const current = state();
            if (current && code && (!archived || current.row.archivedDraftCount > 0))
              this.openFamilyDrafts(code, archived);
          },
          changeVisibility: (request: WorkflowFamilyVisibilityRequest) => {
            const current = state();
            if (
              current && !current.blocked && current.canAuthor &&
              request.detail === current.visibility &&
              request.context === current.visibilityContext
            ) this.visibilityRequested.emit(request);
          },
          refreshVisibility: () => {
            if (state() && code) this.visibilityRefreshRequested.emit(code);
          },
          retryVisibility: () => {
            if (state() && code) this.visibilityRetryRequested.emit(code);
          },
        };
      },
    },
  ];

  readonly familyDefaultColDef: ColDef<WorkflowDefinitionFamilySummary> = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    floatingFilter: false,
    suppressHeaderFilterButton: true,
    suppressKeyboardEvent: ({ event, node }) => {
      if (!isNonInteractiveOpenEvent(event)) return false;
      event.preventDefault();
      if (node.data && !event.repeat) this.selectFamily(node.data);
      return true;
    },
    cellStyle: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  readonly familyGridOptions: GridOptions<WorkflowDefinitionFamilySummary> = {
    defaultColDef: this.familyDefaultColDef,
    rowSelection: {
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    },
    getRowId: ({ data }) => data.workflowCode,
    onGridReady: ({ api }) => {
      if (api.isDestroyed()) return;
      this.familyGridApi = api;
      api.setGridAriaProperty('label', 'Workflows');
    },
    onGridPreDestroyed: ({ api }) => {
      if (this.familyGridApi === api) this.familyGridApi = undefined;
    },
    onRowClicked: ({ data }) => {
      if (data) this.selectFamily(data);
    },
  };

  private familyVisibilityLabel(
    row: WorkflowDefinitionFamilySummary | null | undefined,
  ): string {
    if (!row) return '';
    const detail = this.familyVisibility(row.workflowCode, row.visibility);
    if (
      this.familyPage?.sharedVisibilityAvailable !== true ||
      detail?.isAvailable === false
    )
      return 'Unavailable';
    return detail?.isHidden === true
      ? 'Hidden for all authors'
      : detail?.isHidden === false
        ? 'Visible'
        : 'Not reported';
  }

  readonly columnDefs: ColDef<WorkflowDefinitionLifecycleDetail>[] = [
    {
      headerName: 'Workflow',
      headerComponent: WorkflowCatalogSortLabelComponent,
      flex: 1.45,
      minWidth: 170,
      valueGetter: ({ data }) => workflowIdentitySearchValue(data),
      cellClass: 'workflow-catalog-cell',
      cellRenderer: WorkflowCatalogIdentityCellComponent,
      cellRendererParams: {
        openDefinition: (definition: WorkflowDefinitionLifecycleDetail) =>
          this.selectDefinition(definition),
      },
    },
    {
      field: 'definitionVersionCode',
      headerName: 'Version',
      headerComponent: WorkflowCatalogSortLabelComponent,
      flex: 0.7,
      minWidth: 90,
    },
    {
      headerName: 'Status',
      headerComponent: WorkflowCatalogSortLabelComponent,
      flex: 1.4,
      minWidth: 175,
      valueGetter: ({ data }) =>
        data ? operationalStatus(data).searchValue : '',
      cellClass: 'workflow-catalog-cell',
      cellRenderer: (
        params: ICellRendererParams<WorkflowDefinitionLifecycleDetail>,
      ) => renderOperationalStatus(params.data),
    },
    {
      headerName: 'Usage',
      headerComponent: WorkflowCatalogSortLabelComponent,
      flex: 0.72,
      minWidth: 82,
      valueGetter: ({ data }) =>
        data ? `${data.openInstanceCount} ${data.totalInstanceCount}` : '',
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const openDifference =
          (nodeA.data?.openInstanceCount ?? 0) -
          (nodeB.data?.openInstanceCount ?? 0);
        return openDifference !== 0
          ? openDifference
          : (nodeA.data?.totalInstanceCount ?? 0) -
              (nodeB.data?.totalInstanceCount ?? 0);
      },
      cellClass: 'workflow-catalog-cell',
      cellRenderer: (
        params: ICellRendererParams<WorkflowDefinitionLifecycleDetail>,
      ) => renderUsage(params.data),
    },
    {
      field: 'publishedUtc',
      headerName: 'Published',
      headerComponent: WorkflowCatalogSortLabelComponent,
      flex: 0.82,
      minWidth: 115,
      valueFormatter: ({ value }) => formatDateLabel(value),
    },
    {
      colId: 'workflowActions',
      headerName: 'Actions',
      pinned: 'right',
      lockPinned: true,
      width: 240,
      minWidth: 240,
      maxWidth: 240,
      sortable: false,
      resizable: false,
      cellRenderer: WorkflowVersionActionsCellComponent,
      cellRendererParams: {
        canAuthor: () => this.canAuthor,
        blocked: () =>
          this.loading || this.draftActionBlocked || this.error !== null,
        openDefinition: (definition: WorkflowDefinitionLifecycleDetail) =>
          this.selectDefinition(definition),
        requestDraft: (definition: WorkflowDefinitionLifecycleDetail) =>
          this.requestDraft(definition),
      },
    },
  ];

  readonly defaultColDef: ColDef<WorkflowDefinitionLifecycleDetail> = {
    sortable: true,
    resizable: true,
    suppressMovable: true,
    floatingFilter: false,
    suppressHeaderFilterButton: true,
    suppressKeyboardEvent: (params) => this.onGridKeyboardEvent(params),
    suppressHeaderKeyboardEvent: ({ event }) => isNativeSortActivation(event),
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  readonly gridOptions: GridOptions<WorkflowDefinitionLifecycleDetail> = {
    // The shared grid reapplies gridOptions after initialization. Keep its
    // defaults aligned with the separate defaultColDef input on that update.
    defaultColDef: this.defaultColDef,
    // Keep the native title action present across horizontal scroll positions.
    suppressColumnVirtualisation: true,
    rowSelection: {
      mode: 'singleRow',
      enableClickSelection: false,
      checkboxes: false,
    },
    getRowId: ({ data }) => String(data.id),
    onGridReady: (event) => {
      if (event.api.isDestroyed()) return;
      this.gridApi = event.api;
      event.api.setGridAriaProperty('label', 'Workflow versions');
      this.syncGridSelection();
    },
    onGridPreDestroyed: (event) => {
      if (this.gridApi === event.api) this.gridApi = undefined;
    },
    onRowDataUpdated: () => this.syncGridSelection(),
  };

  get rows(): WorkflowDefinitionLifecycleDetail[] {
    return this.isForbidden ? [] : (this.page?.items ?? []);
  }

  get filterActionButtons(): ButtonConfig[] {
    return [
      {
        id: 'applyCatalogFilters',
        buttonText: this.loading ? 'Loading...' : 'Search',
        disabled: this.loading,
      },
      {
        id: 'clearCatalogFilters',
        buttonText: 'Clear filters',
        severity: 'secondary',
        disabled: this.loading,
      },
      {
        id: 'refreshCatalog',
        buttonText: 'Refresh',
        severity: 'secondary',
        disabled: this.loading,
      },
    ];
  }

  get paginationButtons(): ButtonConfig[] {
    return [
      {
        id: 'previousCatalogPage',
        buttonText: 'Previous',
        severity: 'secondary',
        disabled: this.loading || (this.page?.pageNumber ?? 1) <= 1,
      },
      {
        id: 'nextCatalogPage',
        buttonText: 'Next',
        severity: 'secondary',
        disabled: this.loading || !this.hasNextPage,
      },
    ];
  }

  get hasNextPage(): boolean {
    if (!this.page) {
      return false;
    }

    return this.page.pageNumber * this.page.pageSize < this.page.totalCount;
  }

  get pageRangeLabel(): string {
    const page = this.page;
    if (!page || page.totalCount === 0 || page.items.length === 0) {
      const totalLabel = page?.totalCount === 1 ? 'version' : 'versions';
      return page?.totalCount
        ? `0 of ${page.totalCount} ${totalLabel}`
        : '0 versions';
    }

    const first = (page.pageNumber - 1) * page.pageSize + 1;
    const last = first + page.items.length - 1;
    const totalLabel = page.totalCount === 1 ? 'version' : 'versions';
    return `${first}-${last} of ${page.totalCount} ${totalLabel}`;
  }

  get resultCountLabel(): string {
    const total = this.page?.totalCount ?? 0;
    return total === 1 ? '1 version' : `${total} versions`;
  }

  get hasActiveFilters(): boolean {
    return Boolean(
      this.currentQuery.search ||
      this.currentQuery.workflowCode ||
      this.currentQuery.lifecycleStatusCode ||
      this.currentQuery.activationPostureCode,
    );
  }

  get selectedVersionIsOffPage(): boolean {
    return Boolean(
      this.selectedDefinition && !this.rows.some((row) => this.isSelected(row)),
    );
  }

  get asOfLabel(): string {
    return this.page ? formatDateTimeLabel(this.page.asOfUtc) : '';
  }

  get statusMessage(): string {
    if (this.loading && this.page) {
      return 'Updating versions. Current results remain available.';
    }

    if (this.loading) {
      return 'Loading workflow versions.';
    }

    return '';
  }

  get isForbidden(): boolean {
    return this.error?.statusCode === 401 || this.error?.statusCode === 403;
  }

  get errorMessage(): string {
    if (this.isForbidden) {
      return 'You do not have permission to view workflow versions. Contact an administrator if you need access.';
    }

    return this.rows.length > 0
      ? 'Workflow versions could not be refreshed. Showing the most recently loaded versions. Try again.'
      : 'Workflow versions could not be loaded. Try again.';
  }

  onFilterActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'applyCatalogFilters':
        this.emitFilterQuery();
        break;
      case 'clearCatalogFilters':
        this.clearFilters();
        break;
      case 'refreshCatalog':
        this.retryRequested.emit();
        break;
      default:
        break;
    }
  }

  onPaginationButtonClick(button: ButtonConfig): void {
    const pageNumber = this.page?.pageNumber ?? this.currentQuery.pageNumber;
    switch (button.id) {
      case 'previousCatalogPage':
        this.emitQuery({
          ...this.currentQuery,
          pageNumber: Math.max(1, pageNumber - 1),
        });
        break;
      case 'nextCatalogPage':
        if (this.hasNextPage) {
          this.emitQuery({
            ...this.currentQuery,
            pageNumber: pageNumber + 1,
          });
        }
        break;
      default:
        break;
    }
  }

  onFilterSubmit(event: Event): void {
    event.preventDefault();
    if (!this.loading) {
      this.emitFilterQuery();
    }
  }

  retry(): void {
    if (!this.loading) this.retryRequested.emit();
  }

  toggleAdvancedFilters(): void {
    this.advancedFiltersOpen = !this.advancedFiltersOpen;
  }

  goToFirstPage(): void {
    this.emitQuery({
      ...this.currentQuery,
      pageNumber: 1,
    });
  }

  selectDefinition(definition: WorkflowDefinitionLifecycleDetail): void {
    this.definitionSelected.emit(definition);
  }

  requestDraft(definition: WorkflowDefinitionLifecycleDetail): void {
    if (
      !this.canAuthor ||
      this.loading ||
      this.draftActionBlocked ||
      this.error ||
      !definition.publishedUtc ||
      !['create', 'continue', 'ambiguous'].includes(
        definition.draftActionCode ?? '',
      )
    )
      return;
    this.draftRequested.emit(definition);
  }

  isSelected(definition: WorkflowDefinitionLifecycleDetail): boolean {
    return (
      this.selectedDefinition?.workflowCode === definition.workflowCode &&
      this.selectedDefinition.definitionVersionCode ===
        definition.definitionVersionCode
    );
  }

  private activeGridApi():
    | GridApi<WorkflowDefinitionLifecycleDetail>
    | undefined {
    if (this.gridApi?.isDestroyed()) this.gridApi = undefined;
    return this.gridApi;
  }

  private activeFamilyGridApi():
    | GridApi<WorkflowDefinitionFamilySummary>
    | undefined {
    if (this.familyGridApi?.isDestroyed()) this.familyGridApi = undefined;
    return this.familyGridApi;
  }

  private syncGridSelection(): void {
    this.activeGridApi()?.forEachNode((node) => {
      const shouldBeSelected = node.data ? this.isSelected(node.data) : false;
      if (node.isSelected() !== shouldBeSelected) {
        node.setSelected(shouldBeSelected);
      }
    });
  }

  private onGridKeyboardEvent(
    params: SuppressKeyboardEventParams<WorkflowDefinitionLifecycleDetail>,
  ): boolean {
    if (!isNonInteractiveOpenEvent(params.event)) {
      return false;
    }

    params.event.preventDefault();
    if (params.node.data && !params.event.repeat) {
      this.selectDefinition(params.node.data);
    }
    return true;
  }

  private emitFilterQuery(): void {
    this.emitQuery({
      search: normalizeOptionalText(this.primaryFilterFormValues.search),
      workflowCode: normalizeOptionalText(
        this.advancedFilterFormValues.workflowCode,
      ),
      lifecycleStatusCode: normalizeOptionalText(
        this.primaryFilterFormValues.lifecycleStatusCode,
      ),
      activationPostureCode: normalizeOptionalText(
        this.primaryFilterFormValues.activationPostureCode,
      ),
      pageNumber: 1,
      pageSize: normalizePageSize(this.advancedFilterFormValues.pageSize),
    });
  }

  clearFilters(): void {
    this.primaryFilterFormValues = {
      search: '',
      lifecycleStatusCode: '',
      activationPostureCode: '',
    };
    this.advancedFilterFormValues = {
      workflowCode: '',
      pageSize: normalizePageSize(this.advancedFilterFormValues.pageSize),
    };
    this.advancedFiltersOpen = false;
    this.emitFilterQuery();
  }

  private emitQuery(query: WorkflowDefinitionCatalogQuery): void {
    const normalized = normalizeCatalogQuery(query);
    this.currentQuery = normalized;
    this.queryChange.emit(normalized);
  }
}

export function normalizeCatalogQuery(
  query: WorkflowDefinitionCatalogQuery | null | undefined,
): WorkflowDefinitionCatalogQuery {
  return {
    search: normalizeOptionalText(query?.search),
    workflowCode: normalizeOptionalText(query?.workflowCode),
    lifecycleStatusCode: normalizeOptionalText(query?.lifecycleStatusCode),
    activationPostureCode: normalizeOptionalText(query?.activationPostureCode),
    includeHidden: query?.includeHidden === 'true' ? 'true' : undefined,
    pageNumber: normalizePositiveInteger(query?.pageNumber, 1),
    pageSize: normalizePageSize(query?.pageSize),
  };
}

function normalizePageSize(value: unknown): number {
  const parsed = normalizePositiveInteger(value, DEFAULT_PAGE_SIZE);
  return PAGE_SIZE_OPTIONS.some((option) => option.value === parsed)
    ? parsed
    : DEFAULT_PAGE_SIZE;
}

function normalizePositiveInteger(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function normalizeOptionalText(value: unknown): string | undefined {
  const normalized = normalizeText(value);
  return normalized.length > 0 ? normalized : undefined;
}

function normalizeText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function workflowIdentitySearchValue(
  definition: WorkflowDefinitionLifecycleDetail | null | undefined,
): string {
  if (!definition) {
    return '';
  }

  const displayName = normalizeText(definition.displayName);
  return displayName && displayName !== definition.workflowCode
    ? `${displayName} ${definition.workflowCode}`
    : definition.workflowCode;
}

function isNativeSortActivation(event: KeyboardEvent): boolean {
  return (
    (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') &&
    event.target instanceof Element &&
    event.target.closest('.workflow-catalog-sort-button') !== null
  );
}

function renderOperationalStatus(
  definition: WorkflowDefinitionLifecycleDetail | null | undefined,
): string {
  if (!definition) {
    return '';
  }

  const status = operationalStatus(definition);
  return (
    `<span class="workflow-catalog-status-group" role="group" aria-label="${escapeHtml(status.accessibleLabel)}" title="${escapeHtml(status.accessibleLabel)}">` +
    `<span aria-hidden="true" class="workflow-catalog-status workflow-catalog-status--${status.tone}">${escapeHtml(status.title)}</span>` +
    `<span aria-hidden="true" class="workflow-catalog-cell__secondary"> · ${escapeHtml(status.detail)}</span>` +
    '</span>'
  );
}

function renderUsage(
  definition: WorkflowDefinitionLifecycleDetail | null | undefined,
): string {
  if (!definition) {
    return '';
  }

  if (definition.totalInstanceCount === 0) {
    return `<span class="workflow-catalog-cell__primary">No work yet</span>`;
  }

  const openLabel = `${definition.openInstanceCount} open`;
  const totalLabel = `${definition.totalInstanceCount} total`;
  return (
    `<span class="workflow-catalog-usage" title="${escapeHtml(`${openLabel}, ${totalLabel}`)}" aria-label="${escapeHtml(`${openLabel}, ${totalLabel}`)}">` +
    `<span class="workflow-catalog-cell__primary">${escapeHtml(openLabel)}</span>` +
    `<span class="workflow-catalog-cell__secondary"> · ${escapeHtml(totalLabel)}</span></span>`
  );
}

function operationalStatus(definition: WorkflowDefinitionLifecycleDetail): {
  title: string;
  detail: string;
  tone: 'ready' | 'blocked';
  searchValue: string;
  accessibleLabel: string;
} {
  if (definition.isExecutable) {
    return {
      title: 'Available for new work',
      detail: 'Active and ready',
      tone: 'ready',
      searchValue: 'Available for new work Active and ready',
      accessibleLabel: 'Available for new work. Active and ready.',
    };
  }

  const reasonLabels: Record<string, string> = {
    lifecycle_not_active: 'Version is not active',
    runtime_unbound: 'Runtime setup incomplete',
    runtime_invalid: 'Workflow has validation errors',
    effective_window_future: 'Activation is scheduled',
    effective_window_expired: 'Activation has ended',
    activation_missing: 'Version is not activated',
  };
  const reasonCodes = new Set(
    definition.executabilityReasonCodes.map((reason) =>
      normalizeExecutabilityReasonCode(reason),
    ),
  );
  const reasonPriority = [
    'effective_window_future',
    'effective_window_expired',
    'activation_missing',
    'runtime_invalid',
    'runtime_unbound',
    'lifecycle_not_active',
  ];
  const hasSpecificActivationReason =
    reasonCodes.has('effective_window_future') ||
    reasonCodes.has('effective_window_expired') ||
    reasonCodes.has('activation_missing');
  const reasons = reasonPriority
    .filter(
      (reason) =>
        reason !== 'lifecycle_not_active' || !hasSpecificActivationReason,
    )
    .filter((reason) => reasonCodes.has(reason))
    .map((reason) => reasonLabels[reason]);
  const hasUnknownReason = [...reasonCodes].some(
    (reason) => reason.length > 0 && reasonLabels[reason] === undefined,
  );
  if (hasUnknownReason) {
    reasons.push('Additional configuration details need review');
  }
  if (reasons.length === 0) {
    reasons.push(deriveOperationalStatusDetail(definition));
  }

  const additionalReasonCount = reasons.length - 1;
  const detail =
    additionalReasonCount > 0
      ? `${reasons[0]} · ${additionalReasonCount} more ${additionalReasonCount === 1 ? 'reason' : 'reasons'}`
      : reasons[0];
  const accessibleLabel = `Cannot start new work. ${reasons.join('. ')}.`;

  return {
    title: 'Cannot start new work',
    detail,
    tone: 'blocked',
    searchValue: accessibleLabel,
    accessibleLabel,
  };
}

function normalizeExecutabilityReasonCode(value: unknown): string {
  const normalized = normalizeText(value).toLocaleLowerCase();
  const prefix = 'workflow.definition.';
  return normalized.startsWith(prefix)
    ? normalized.slice(prefix.length)
    : normalized;
}

function deriveOperationalStatusDetail(
  definition: WorkflowDefinitionLifecycleDetail,
): string {
  if (
    normalizeText(definition.validationStatusCode).toLocaleLowerCase() !==
    'valid'
  ) {
    return 'Workflow has validation errors';
  }

  if (
    normalizeText(definition.runtimeBindingCode).toLocaleLowerCase() !==
    'runtime_bound'
  ) {
    return 'Runtime setup incomplete';
  }

  const activation = normalizeText(
    definition.activationPostureCode,
  ).toLocaleLowerCase();
  switch (activation) {
    case 'scheduled':
      return 'Activation is scheduled';
    case 'expired':
      return 'Activation has ended';
    case 'retired':
      return 'Activation is retired';
    case 'not_activated':
      return 'Version is not activated';
    default:
      break;
  }

  if (
    normalizeText(definition.lifecycleStatusCode).toLocaleLowerCase() !==
    'active'
  ) {
    return 'Version is not active';
  }

  return 'Review version details';
}

function formatDateLabel(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    return 'Not published';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

function formatDateTimeLabel(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    return 'Not published';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function isNonInteractiveOpenEvent(event: KeyboardEvent): boolean {
  if (event.key !== 'Enter' && event.key !== ' ' && event.code !== 'Space') {
    return false;
  }

  return !(
    event.target instanceof Element &&
    event.target.closest(
      "button, a, input, select, textarea, [role='button'], [role='link']",
    )
  );
}
